import { useState, useEffect, useCallback, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { getCweTags } from "sj-ui-toolkit/src/api";
import { mapCollectionsToCards, mapLessonsToCards } from "sj-ui-toolkit/src/atlas/utils";
import { useLevelContext } from "sj-ui-toolkit/src/atlas/contexts";
import { difficultyOrder } from "sj-ui-toolkit/src/constants";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getContentClient } from "sj-ui-toolkit/src/client";

import FilterPanel from "./components/FilterPanel/FilterPanel";
import TagBrowser from "./components/TagBrowser/TagBrowser";
import CatalogResults from "./components/CatalogResults/CatalogResults";

import S from "./Catalog.module.scss";

const SESSION_STORAGE_KEY = "catalogFiltersAndSearch";

const Catalog = () => {
  useBreadcrumb({ label: "Catalog", route: "/catalog" });

  const searchInputRef = useRef(null);
  const [tags, setTags] = useState({});
  const { levels } = useLevelContext();

  // Initialize state from sessionStorage (or defaults if they dont exist)
  const getInitialState = () => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        searchQuery: parsed.searchQuery || "",
        filters: parsed.filters || {
          focus: [],
          difficulty: [],
          release: [],
          contentType: [],
          languages: [],
          technologies: [],
          cwe: [],
        },
      };
    }
    return {
      searchQuery: "",
      filters: {
        focus: [],
        difficulty: [],
        release: [],
        contentType: [],
        languages: [],
        technologies: [],
        cwe: [],
      },
    };
  };

  const initialState = getInitialState();
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(initialState.filters);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [cweTags, setCweTags] = useState([]);
  const [results, setResults] = useState([]);
  const [noResults, setNoResults] = useState(false);

  const lessonContentClient = getContentClient();

  // Save to sessionStorage whenever filters or searchQuery change
  useEffect(() => {
    const stateToSave = { searchQuery, filters };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
  }, [searchQuery, filters]);

  // Focus search input if navigated from nav search
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.focusSearch && searchInputRef.current) {
        // Small delay to ensure input is rendered
        setTimeout(() => {
          searchInputRef.current?.focus();
          // Clear the focus flag after focusing
          const { focusSearch: _, ...rest } = parsed;
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(rest));
        }, 100);
      }
    }
  }, []);

  function organizeTagData(data) {
    const sortedData = { ...data };

    // Sort CWE array numerically
    if (sortedData.cwe?.length) {
      sortedData.cwe.sort((a, b) => parseInt(a) - parseInt(b));
    }

    // Sort difficulty array with specific order
    if (sortedData.difficulty?.length) {
      sortedData.difficulty.sort((a, b) => {
        const indexA = difficultyOrder.indexOf(a);
        const indexB = difficultyOrder.indexOf(b);

        // Both in priority list
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;

        // Only one in priority list
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // Neither in priority list - alphabetical
        return a.localeCompare(b);
      });
    }

    return sortedData;
  }

  // Fetch tags on mount
  useEffect(() => {
    const loadTags = async () => {
      setLoadingTags(true);
      try {
        const tagsResponse = await lessonContentClient.getTags();
        setTags(organizeTagData(tagsResponse));
      } catch (error) {
        console.error("Failed to fetch tags:", error);
      } finally {
        setLoadingTags(false);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    const loadCweTags = async () => {
      // Only process when we have both the CWE keys from tags and the CWE data
      if (tags.cwe && tags.cwe.length > 0) {
        const cweData = await getCweTags();

        // Map the CWE numbers to objects with name and description
        const mappedCweTags = tags.cwe.map(cweNumber => {
          const cweInfo = cweData[cweNumber];
          return {
            value: cweNumber,
            name: cweInfo ? `CWE-${cweNumber}: ${cweInfo.Name}` : `CWE-${cweNumber}`,
            description: cweInfo?.Description || null,
          };
        });

        setCweTags(mappedCweTags);
      }
    };
    loadCweTags();
  }, [tags.cwe]);

  // Build base query params shared by both endpoints
  const buildBaseQueryParams = useCallback(() => {
    const params = {};
    const tags = [];

    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    // Add language tags
    filters.languages.forEach(lang => {
      tags.push(`languages:${lang}`);
    });

    // Add technology tags
    filters.technologies.forEach(tech => {
      tags.push(`technologies:${tech}`);
    });

    // Add focus tags
    filters.focus.forEach(focus => {
      tags.push(`focus:${focus}`);
    });

    // Add CWE tags
    filters.cwe.forEach(cwe => {
      tags.push(`cwe:${cwe}`);
    });

    // Release filters map to booleans
    if (filters.release.includes("recently-released")) {
      params.recentlyReleased = true;
    }
    if (filters.release.includes("recently-refreshed")) {
      params.recentlyRefreshed = true;
    }

    return { params, tags };
  }, [searchQuery, filters]);

  // Build params specifically for the lessons endpoint
  const buildLessonParams = useCallback(() => {
    const { params, tags } = buildBaseQueryParams();
    const lessonTags = [...tags];

    // Lessons: difficulty is a tag by name
    if (filters.difficulty.length > 0) {
      lessonTags.push(`difficulty:${filters.difficulty[0]}`);
    }

    // Lessons: content type uses lessonTypes
    if (filters.contentType.length > 0) {
      const handsOnFilters = filters.contentType.filter(type => type.startsWith("HandsOnLesson"));

      if (handsOnFilters.length === 1) {
        params.hasTest = handsOnFilters[0] !== "HandsOnLessonWithoutTests";
      }

      // Replace HandsOnLessonWithoutTests with HandsOnLesson if it's selected without HandsOnLesson
      const lessonTypes = filters.contentType.map(type =>
        type === "HandsOnLessonWithoutTests" ? "HandsOnLesson" : type
      );
      // Remove duplicates in case both were selected
      params.type = [...new Set(lessonTypes)];
    }

    if (lessonTags.length > 0) {
      params.tags = lessonTags;
    }

    return params;
  }, [buildBaseQueryParams, filters.difficulty, filters.contentType]);

  // Build params specifically for the collections endpoint
  const buildCollectionParams = useCallback(() => {
    const { params, tags } = buildBaseQueryParams();

    // Collections: difficulty maps to levelId (1-5)
    if (filters.difficulty.length > 0) {
      const difficultyMap = {
        basic: 1,
        intermediate: 2,
        advanced: 3,
        awareness: 4,
      };
      params.levelId = difficultyMap[filters.difficulty[0]];
    }

    // Collections: content type uses lessonTypes
    if (filters.contentType.length > 0) {
      // Replace HandsOnLessonWithoutTests with HandsOnLesson (not a valid API type)
      const lessonTypes = filters.contentType.map(type =>
        type === "HandsOnLessonWithoutTests" ? "HandsOnLesson" : type
      );
      // Remove duplicates in case both were selected
      params.lessonTypes = [...new Set(lessonTypes)];
    }

    if (tags.length > 0) {
      params.tags = tags;
    }

    return params;
  }, [buildBaseQueryParams, filters.difficulty, filters.contentType]);

  useEffect(() => {
    // Track if this effect has been cleaned up to prevent stale updates
    let cancelled = false;

    const timeoutId = setTimeout(() => {
      const lessonParams = buildLessonParams();
      const collectionParams = buildCollectionParams();

      // Check if either has actual filters applied
      const hasFilters =
        Object.keys(lessonParams).length !== 0 || Object.keys(collectionParams).length !== 0;

      if (hasFilters) {
        // Clear previous results and reset state before fetching
        setResults([]);
        setIsLoading(true);
        setNoResults(false);

        // Track resolution state for each API call
        let collectionsResolved = false;
        let lessonsResolved = false;

        // Store results in closure variables to avoid race conditions.
        // If both APIs resolve simultaneously, React's state batching could cause
        // one update to overwrite the other if we relied on `prev` state.
        let collectionsData = [];
        let lessonsData = [];

        const checkCompletion = () => {
          if (cancelled) return;

          // Turn off loading as soon as either API returns data
          if (collectionsData.length > 0 || lessonsData.length > 0) {
            setIsLoading(false);
          }

          // Only show "no results" once both APIs have resolved with empty data
          if (collectionsResolved && lessonsResolved) {
            setIsLoading(false);
            if (collectionsData.length === 0 && lessonsData.length === 0) {
              setNoResults(true);
            }
          }
        };

        // Fire both API calls simultaneously (not awaiting one before the other)
        lessonContentClient
          .getCollections(collectionParams)
          .then(collections => {
            if (cancelled) return;
            const formattedCollections = mapCollectionsToCards(collections, levels);
            collectionsData = formattedCollections;
            // Merge with whatever lesson data we have so far
            setResults([...formattedCollections, ...lessonsData]);
          })
          .catch(error => {
            console.error("Failed to fetch collections:", error);
          })
          .finally(() => {
            collectionsResolved = true;
            checkCompletion();
          });

        lessonContentClient
          .getLessons(lessonParams)
          .then(lessons => {
            if (cancelled) return;
            const formattedLessons = mapLessonsToCards(lessons);
            lessonsData = formattedLessons;
            // Merge with whatever collection data we have so far
            setResults([...collectionsData, ...formattedLessons]);
          })
          .catch(error => {
            console.error("Failed to fetch lessons:", error);
          })
          .finally(() => {
            lessonsResolved = true;
            checkCompletion();
          });
      } else {
        setResults([]);
        setIsLoading(false);
        setNoResults(false);
      }
    }, 1000);

    // Cleanup: cancel pending timeout and mark as stale to ignore in-flight responses
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [buildLessonParams, buildCollectionParams]);

  // Handle search on Enter key
  const handleKeyDown = e => {
    if (e.key === "Enter") {
      // Trigger search immediately by clearing debounce
      setIsFiltersOpen(true);
    }
  };

  const handleFilterChange = (category, values) =>
    setFilters(prev => ({ ...prev, [category]: values }));

  const handleClearFilters = () => {
    setFilters({
      focus: [],
      difficulty: [],
      release: [],
      contentType: [],
      languages: [],
      technologies: [],
      cwe: [],
    });
  };

  // Handle technology tag selection from TagBrowser
  const handleTechnologySelect = tech => {
    const currentTechs = filters.technologies;
    const newTechs = currentTechs.includes(tech)
      ? currentTechs.filter(t => t !== tech)
      : [...currentTechs, tech];
    handleFilterChange("technologies", newTechs);
    if (!isFiltersOpen) {
      setIsFiltersOpen(true);
    }
  };

  // Handle language tag selection from TagBrowser
  const handleLanguageSelect = lang => {
    const currentLangs = filters.languages;
    const newLangs = currentLangs.includes(lang)
      ? currentLangs.filter(l => l !== lang)
      : [...currentLangs, lang];
    handleFilterChange("languages", newLangs);
    if (!isFiltersOpen) {
      setIsFiltersOpen(true);
    }
  };

  // Toggle filters panel
  const toggleFilters = () => {
    setIsFiltersOpen(prev => !prev);
  };

  const hasActiveFilters = Object.values(filters).some(arr => arr.length > 0);

  return (
    <div className={S.catalog}>
      <div className={S.heroSection}>
        <div className={S.searchContainer}>
          <h1 className="sr-only">Catalog</h1>
          <label htmlFor="catalog-search" className={S.searchLabel}>
            Search the entire learning catalog
          </label>
          <div className={S.searchInputWrapper}>
            <InputText
              id="catalog-search"
              ref={searchInputRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What do you want to learn?"
              className={S.searchInput}
            />
            <Button
              icon="pi pi-search"
              className={S.searchButton}
              onClick={() => setIsFiltersOpen(true)}
              aria-label="Search catalog"
            />
          </div>
        </div>
      </div>

      <div className={S.contentSection}>
        <FilterPanel
          isOpen={isFiltersOpen}
          onToggle={toggleFilters}
          tags={tags}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          cweTags={cweTags}
        />

        {!isFiltersOpen && !hasActiveFilters && !loadingTags && !results.length && (
          <TagBrowser
            technologies={tags.technologies || []}
            languages={tags.languages || []}
            onTechnologySelect={handleTechnologySelect}
            onLanguageSelect={handleLanguageSelect}
            selectedTechnologies={filters.technologies}
            selectedLanguages={filters.languages}
          />
        )}

        <CatalogResults isLoading={isLoading} results={results} noResults={noResults} />
      </div>
    </div>
  );
};

export default Catalog;
